import { useEffect, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import usePricing from 'api/usePricing';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import EditOfferDesignModal from 'components/EditOfferDesignModal/EditOfferDesignModal';

import useOffers from '../../api/useOffers';
import { Offer } from '../../common/contracts';
import ActionCell from '../../components/DataTableComponent/components/ActionCell';
import DataTable from '../../components/DataTableComponent/DataTable';
import DialogModal from '../../components/DialogModal/DialogModal';
import EmptyState from '../../components/EmptyState/EmptyState';
import FilterSearchCard from '../../components/Filter/FilterSearchCard';
import PortalHeader from '../../components/PortalHeader/PortalHeader';
import SortableHeader from '../../components/SortableHeader/SortableHeader';
import { DATE_TIME_FORMAT } from '../../constants/constants';
import {
  OfferType,
  SortingOffersValue
} from '../../constants/enums';
import { usePaginationFromURL } from '../../hooks/usePaginationFromURL';
import { useTableSorting } from '../../hooks/useTableSorting';
import { getConvertedPrice } from '../../utils/getConvertedPrice';
import { permissionsUtil } from '../../utils/permissionsUtil';
import { EToasterStatus, showToast } from '../../utils/showToast';
import { renderActiveStatusChip } from '../../utils/statusChipUtils';

const dayjsUTC = dayjs.extend(utc);

const BundlesTable = () => {
  const { publisherId } = useParams();
  const { getPricings } = usePricing();
  const navigate = useNavigate();
  const location = useLocation();
  const { page, rows, sortValue, direction } = usePaginationFromURL('offers');
  const [currentPage, setCurrentPage] = useState(page);
  const [rowsPerPage, setRowsPerPage] = useState(rows);
  const { sorting, updateSorting } = useTableSorting(
    SortingOffersValue.CREATED_AT
  );
  const { getOffers, deleteOffer, toggleOfferStatus } = useOffers({
    offerType: OfferType.BUNDLE,
    customPagination: { currentPage, rowsPerPage },
    sorting
  });
  const totalCount = getOffers.data?.totalCount || 0;
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);
  const [externalIdFilter, setExternalIdFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [showLimitedEditModal, setShowLimitedEditModal] = useState(false);

  const canManageOffers = permissionsUtil.isActionEnabled(
    null,
    EPermissionGroup.OFFERS,
    EPermissionAction.MANAGE,
    publisherId
  );

  useEffect(() => {
    const queryParams = new URLSearchParams();
    queryParams.set('page', String(currentPage));
    queryParams.set('rows', String(rowsPerPage));
    queryParams.set('sortValue', sorting.sortValue);
    queryParams.set('direction', sorting.direction);

    navigate(`${location.pathname}?${queryParams.toString()}`, {
      replace: true
    });
    getOffers.refetch();
    getPricings.refetch();
  }, [currentPage, rowsPerPage, sorting]);

  const handleDeleteOffer = async () => {
    if (!selectedOffer) return;
    deleteOffer.mutate(selectedOffer, {
      onSuccess: () => {
        showToast({
          message: 'Offer deleted successfully',
          status: EToasterStatus.SUCCESS
        });
        getOffers.refetch();
      },
      onError: (e: any) => {
        showToast({
          message: 'Failed to delete offer',
          status: EToasterStatus.ERROR,
          description: e.response.data.message ?? ''
        });
      },
      onSettled: () => {
        setIsDeleteDialogOpen(false);
        setSelectedOffer(null);
      }
    });
  };

  const toggleBundleStatus = async (offerId: string, active: boolean) => {
    toggleOfferStatus.mutate(
      { active: !active, offerId },
      {
        onSuccess: () => {
          showToast({
            message: active
              ? 'Bundle deactivated successfully'
              : 'Bundle activated successfully',
            status: EToasterStatus.SUCCESS
          });
          getOffers.refetch();
        },
        onError: () => {
          showToast({
            message: 'Error editing bundle',
            status: EToasterStatus.ERROR
          });
        }
      }
    );
  };

  const handleEditOfferUI = (offerId: string): void => {
    navigate(`./form/${offerId}`);
  };

  const handleDuplicateOffer = (offerId: string): void => {
    navigate(`./dup/${offerId}`);
  };

  const handleDeletePopup = (offerId: string) => {
    if (!offerId) return;
    setSelectedOffer(offerId);
    setIsDeleteDialogOpen(true);
  };

  const COLUMNS: any = [
    {
      accessorKey: 'offerId',
      header: 'ID',
      cell: ({ getValue }: any) => getValue()
    },
    {
      accessorKey: 'publisherOfferId',
      header: 'External ID',
      cell: ({ getValue }: any) => getValue()
    },
    {
      accessorKey: 'offerUi.name',
      header: 'Offer Design',
      cell: ({ row }: any) => {
        if (getOffers.isLoading) return '';
        return <span>{row.original.offerUi?.name}</span>;
      }
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ getValue }: any) => getValue()
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <SortableHeader
          label="Created at"
          sortValue={SortingOffersValue.CREATED_AT}
          currentSortValue={sorting.sortValue}
          currentDirection={sorting.direction}
          onSort={updateSorting}
        />
      ),
      cell: ({ row }: any) => {
        if (getOffers.isLoading) return '';
        return (
          <span>
            {dayjsUTC.utc(row.original.createdAt).format(DATE_TIME_FORMAT)}
          </span>
        );
      }
    },
    {
      accessorKey: 'price',
      header: 'Price',
      cell: ({ row }: any) => {
        if (getOffers.isLoading || getPricings.isLoading) return '';
        return getConvertedPrice(row.original, getPricings.data.result);
      }
    },
    {
      accessorKey: 'active',
      header: 'Status',
      accessorFn: (row: any) => {
        return row.active ? 'Active' : 'Inactive';
      },
      cell: ({ row }: any) => {
        return renderActiveStatusChip(row.original.active);
      }
    },
    {
      accessorKey: 'actions',
      header: null,
      maxSize: 60,
      minSize: 60,
      size: 60,
      cell: ({ row }: any) => (
        <div className="max-w-[60px] w-[60px]">
          <ActionCell
            rowData={row.original}
            actions={[
              {
                label: 'Edit',
                handler: () => {
                  handleEditOfferUI(row.original.offerId);
                },
                disabled:
                  !permissionsUtil.canUserEdit() ||
                  !permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.MANAGE,
                    publisherId
                  ),
                hidden: !permissionsUtil.isActionEnabled(
                  null,
                  EPermissionGroup.OFFERS,
                  EPermissionAction.MANAGE,
                  publisherId
                )
              },
              {
                label: 'Edit',
                handler: () => {
                  setSelectedOffer(row.original.offerId);
                  setShowLimitedEditModal(true);
                },
                disabled:
                  !permissionsUtil.canUserEdit() ||
                  !permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.UPDATE_OFFER_STATUS_DESIGN,
                    publisherId
                  ),
                hidden:
                  !permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.UPDATE_OFFER_STATUS_DESIGN,
                    publisherId
                  ) ||
                  permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.MANAGE,
                    publisherId
                  )
              },
              {
                label: row.original.active ? 'Deactivate' : 'Activate',
                handler: () =>
                  toggleBundleStatus(row.original.offerId, row.original.active),
                disabled:
                  !permissionsUtil.canUserEdit() ||
                  !permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.UPDATE_OFFER_STATUS,
                    publisherId
                  )
              },
              {
                label: 'Duplicate',
                handler: () => handleDuplicateOffer(row.original.offerId),
                disabled:
                  !permissionsUtil.canUserEdit() ||
                  !permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.MANAGE,
                    publisherId
                  )
              },
              {
                label: 'Delete',
                handler: () => handleDeletePopup(row.original.offerId),
                disabled:
                  !permissionsUtil.canUserEdit() ||
                  !permissionsUtil.isActionEnabled(
                    null,
                    EPermissionGroup.OFFERS,
                    EPermissionAction.MANAGE,
                    publisherId
                  )
              }
            ]}
          />
        </div>
      )
    }
  ];

  const offers =
    !getOffers.isLoading && getOffers.data && getPricings.data
      ? getOffers.data.offers
        ? getOffers.data.offers.map((offer: Offer) => ({
          // New format
          _id: offer.publisherOfferId,
          ...offer
        }))
        : getOffers.data.result
          .filter((offer: Offer) => offer.type === 'Bundle') // Old format
          .map((offer: Offer) => ({
            _id: offer.publisherOfferId,
            ...offer
          }))
      : [];

  const filteredOffers = offers.filter((offer: Offer) => {
    const lowerExternalId = offer.publisherOfferId.toLowerCase();
    const lowerName = offer.name.toLowerCase();
    return (
      lowerExternalId.includes(externalIdFilter.toLowerCase()) &&
      lowerName.includes(nameFilter.toLowerCase())
    );
  });

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const BUTTON_TEXT = `${offers.length > 0 ? 'Add' : 'Create'} New Bundle`;

  const renderContent = () => {
    if (offers.length === 0 && !getOffers.isLoading && !getOffers.isFetching) {
      return (
        <EmptyState
          title="Add your first Bundle"
          description="Bundles include one or more products sold at a discounted rate or with added value."
          buttonText={BUTTON_TEXT}
          className="mx-auto mt-36"
          onClick={() => navigate('./form')}
          disableButton={!canManageOffers}
        />
      );
    }

    return (
      <div className="border border-base-sidebarBorder rounded-lg mx-7 mb-9">
        <DataTable
          data={filteredOffers}
          columns={COLUMNS}
          rowIdIdentifier="offerId"
          bodyDataTestId="dashBundlesList"
          rowCount={totalCount}
          rowsPerPage={rowsPerPage}
          showPagination={totalPages > 1}
          manualPagination
          pageIndex={currentPage}
          pageCount={totalPages}
          onPageChange={setCurrentPage}
          isLoading={getOffers.isLoading || getOffers.isFetching}
        />
      </div>
    );
  };

  return (
    <div className="overflow-y-scroll">
      <PortalHeader
        title="Bundles"
        primaryButtonText={BUTTON_TEXT}
        onPrimaryButtonClick={() => navigate('./form')}
        primaryButtonState={{
          disabled: !permissionsUtil.canUserEdit(),
          hidden: !canManageOffers
        }}
        isCreateButton
      />
      {offers.length > 0 && (
        <div className="flex px-7 gap-3 mb-5">
          <FilterSearchCard
            header="Bundles External ID"
            filterValue={externalIdFilter}
            setFilterValue={setExternalIdFilter}
            isLabelSearchBy
          />
          <FilterSearchCard
            header="Bundles Name"
            filterValue={nameFilter}
            setFilterValue={setNameFilter}
          />
        </div>
      )}
      {renderContent()}
      {showLimitedEditModal && (
        <EditOfferDesignModal
          offerId={selectedOffer || ''}
          offerDesignType={OfferDesignTypes.Bundle}
          onClose={() => setShowLimitedEditModal(false)}
          isOpen={showLimitedEditModal}
          offerType={OfferType.BUNDLE}
        />
      )}
      {isDeleteDialogOpen && (
        <DialogModal
          isOpen={isDeleteDialogOpen}
          title="Delete Bundle"
          description="Are you sure you want to delete this bundle?"
          modalActions={{
            onConfirm: { label: 'Delete', action: handleDeleteOffer },
            onClose: {
              label: 'Cancel',
              action: () => setIsDeleteDialogOpen(false),
              variant: 'link'
            }
          }}
        />
      )}
    </div>
  );
};

export default BundlesTable;
